import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { apiClient } from "../services/api/client";
import { AUTH_SESSION_CLEARED_EVENT, AUTH_SESSION_UPDATED_EVENT, sessionStorageService, } from "./session";
const AuthContext = createContext(null);
const sessionHasTradingAccess = (s) => s?.user.activation_status === "active";
const sessionIsAdmin = (s) => Boolean(s?.user.roles?.includes("admin"));
/** Проверить access token; при 401 — refresh; при неудаче очистить сессию. */
async function resolveSession(stored) {
    const me = await apiClient.auth.me();
    if (me.success) {
        const next = { ...stored, user: me.data };
        sessionStorageService.set(next);
        return next;
    }
    if (me.error.code !== "UNAUTHORIZED" && !me.error.code.startsWith("HTTP_401")) {
        return stored;
    }
    const refreshed = await apiClient.auth.refresh(stored.refresh_token);
    if (refreshed.success) {
        sessionStorageService.set(refreshed.data);
        return refreshed.data;
    }
    sessionStorageService.clear();
    return null;
}
export const AuthProvider = ({ children }) => {
    const [session, setSession] = useState(null);
    const [isAuthReady, setIsAuthReady] = useState(false);
    const refreshUser = useCallback(async () => {
        const current = sessionStorageService.get();
        if (!current)
            return;
        const next = await resolveSession(current);
        if (!next) {
            setSession(null);
            return;
        }
        setSession(next);
    }, []);
    useEffect(() => {
        const onUpdated = (event) => {
            const detail = event.detail;
            if (detail) {
                setSession(detail);
            }
        };
        const onCleared = () => setSession(null);
        window.addEventListener(AUTH_SESSION_UPDATED_EVENT, onUpdated);
        window.addEventListener(AUTH_SESSION_CLEARED_EVENT, onCleared);
        return () => {
            window.removeEventListener(AUTH_SESSION_UPDATED_EVENT, onUpdated);
            window.removeEventListener(AUTH_SESSION_CLEARED_EVENT, onCleared);
        };
    }, []);
    useEffect(() => {
        const s = sessionStorageService.get();
        if (!s) {
            setIsAuthReady(true);
            return;
        }
        void (async () => {
            try {
                const next = await resolveSession(s);
                setSession(next);
            }
            finally {
                setIsAuthReady(true);
            }
        })();
    }, []);
    /** Авто-проверка брокерских токенов при активной сессии (мок: validate по каждой записи). */
    useEffect(() => {
        if (!isAuthReady || !session || session.user.activation_status !== "active")
            return;
        void (async () => {
            const list = await apiClient.tokens.list();
            if (!list.success)
                return;
            for (const tok of list.data) {
                await apiClient.tokens.validate(tok.id);
            }
        })();
    }, [isAuthReady, session?.user.id, session?.user.activation_status]);
    const login = useCallback(async (email, password) => {
        const result = await apiClient.auth.login(email, password);
        if (!result.success)
            return { ok: false, message: result.error.message };
        setSession(result.data);
        sessionStorageService.set(result.data);
        return { ok: true, user: result.data.user };
    }, []);
    const register = useCallback(async (email, password, name) => {
        const result = await apiClient.auth.register(email, password, name);
        if (!result.success)
            return { ok: false, message: result.error.message };
        setSession(result.data);
        sessionStorageService.set(result.data);
        return { ok: true, user: result.data.user };
    }, []);
    const logout = () => {
        setSession(null);
        sessionStorageService.clear();
    };
    const canAccessTrading = sessionHasTradingAccess(session);
    const isAdmin = sessionIsAdmin(session);
    const value = useMemo(() => ({
        session,
        isAuthReady,
        isAuthenticated: Boolean(session),
        canAccessTrading,
        isAdmin,
        login,
        register,
        logout,
        refreshUser,
    }), [session, isAuthReady, canAccessTrading, isAdmin, login, register, refreshUser]);
    return _jsx(AuthContext.Provider, { value: value, children: children });
};
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context)
        throw new Error("useAuth must be used inside AuthProvider");
    return context;
};
